import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { CollapsibleMarkdownEditor } from '@/components/admin/shared/collapsible-markdown-editor'
import { ComplexityAnalysis } from '@/components/ui/complexity-analysis'
import { Copyright } from '@/components/ui/copyright'
import { HighlightedCodeBlock } from '@/components/ui/highlighted-code-block'
import { Image } from '@/components/ui/image'
import {
  REVEAL_ON_HOVER,
  REVEAL_ON_HOVER_DECORATIVE,
} from '@/components/ui/reveal-on-hover'
import { SyntaxHighlightedMarkdown } from '@/components/ui/syntax-highlighted-markdown'
import type { ForceExpandState } from '@/hooks/use-force-expand-state'
import { parseLanguageTags } from '@/lib/editorial'
import { cn } from '@/lib/utils'
import { Badge } from '../../shared/badge'
import { SOLUTION_TYPES } from '../constants'
import type {
  EditorialBlock,
  QuestionApproach,
  SolutionLanguage,
} from '../types'
import { SUPPORTED_SOLUTION_LANGUAGES } from '../types'
import { ApproachBlockEditor } from './approach-block-editor'
import type { RecipeSummary } from './recipe-block-editor'
import { RecipeCallout, type RecipeForCallout } from './recipe-callout'

// =============================================================================
// Types
// =============================================================================

interface ApproachHint {
  id?: string
  content: string
  order: number
}

type ApproachWithHints = QuestionApproach & {
  hints?: ApproachHint[]
}

interface EditorialSectionProps {
  /** Editorial format: 'new' (DB-driven) or 'legacy' (synced from bctci-code) */
  editorialFormat?: 'new' | 'legacy'
  /** Callback to update editorial format */
  onUpdateEditorialFormat?: (format: 'new' | 'legacy') => void
  editorialBlocks: EditorialBlock[]
  approaches: ApproachWithHints[]
  onUpdateBlocks: (blocks: EditorialBlock[]) => void
  /** Callback to update approaches - required for approach editing */
  onUpdateApproaches?: (approaches: ApproachWithHints[]) => void
  /** All available strategies for tagging */
  allStrategies?: { id: string; name: string; slug: string }[]
  /** All available recipes for recipe block selection and preview */
  allRecipes?: RecipeSummary[]
  /** Full recipe data for preview callouts (includes implementations) */
  recipesForPreview?: RecipeForCallout[]
  /** Problem slug for running tests */
  problemSlug?: string
  /** Function name override from test config */
  functionNameOverride?: string
}

type TabId = 'edit' | 'preview'

/**
 * Convert <Image src="..." alt="..." /> JSX tags to standard markdown image
 * syntax so react-markdown can render them in the preview.
 */
function preprocessImageTags(markdown: string): string {
  return markdown.replace(/<Image\s+([^>]*?)\s*\/?>/gi, (match, attrs) => {
    const srcMatch = /src=["']([^"']+)["']/.exec(attrs)
    const altMatch = /alt=["']([^"']*?)["']/.exec(attrs)
    if (srcMatch) {
      return `![${altMatch?.[1] || ''}](${srcMatch[1]})`
    }
    return match
  })
}

const previewMarkdownComponents = {
  img: ({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) => (
    <Image src={src || ''} alt={alt} />
  ),
}

// =============================================================================
// Sortable Block Wrapper
// =============================================================================

function SortableBlock({
  id,
  children,
}: {
  id: string
  children: (props: {
    dragHandleProps: Record<string, unknown>
    isDragging: boolean
  }) => React.ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div ref={setNodeRef} style={style}>
      {children({
        dragHandleProps: { ...attributes, ...listeners },
        isDragging,
      })}
    </div>
  )
}

// =============================================================================
// Insert Button (between blocks)
// =============================================================================

function InsertButton({
  onAddMarkdown,
  onAddNewApproach,
}: {
  onAddMarkdown: () => void
  onAddNewApproach: () => void
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleBlur = useCallback((e: React.FocusEvent) => {
    // Close if focus leaves the container entirely
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget)
    ) {
      setOpen(false)
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className="group relative flex items-center justify-center py-1"
      onBlur={handleBlur}
    >
      {/* Hover line */}
      {/*
        Decorative, and absolutely positioned out of the flow — no
        pointer-events juggling needed, but it still reveals on focus-within so
        the insert affordance reads the same to a keyboard user as to a mouse.
      */}
      <div
        className={cn(
          REVEAL_ON_HOVER_DECORATIVE,
          'bg-border absolute inset-x-0 top-1/2 h-px -translate-y-1/2',
        )}
      />

      {/* + button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'border-border bg-card hover:bg-muted relative z-10 flex h-6 w-6 items-center justify-center rounded-full border transition-all',
          open ? 'opacity-100' : REVEAL_ON_HOVER,
        )}
      >
        <Plus className="h-3 w-3" />
      </button>

      {/* Dropdown menu */}
      {open && (
        <div className="border-border bg-card absolute top-full z-20 mt-1 w-56 rounded-lg border shadow-lg">
          <button
            type="button"
            onClick={() => {
              onAddMarkdown()
              setOpen(false)
            }}
            className="hover:bg-muted w-full px-3 py-2 text-left text-sm transition-colors first:rounded-t-lg"
          >
            Add Markdown
          </button>
          <button
            type="button"
            onClick={() => {
              onAddNewApproach()
              setOpen(false)
            }}
            className="hover:bg-muted w-full px-3 py-2 text-left text-sm transition-colors last:rounded-b-lg"
          >
            Add Approach
          </button>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Inline Approach Block Card (expandable with editing)
// =============================================================================

function InlineApproachBlockCard({
  approach,
  approachIndex,
  dragHandleProps,
  isExpanded,
  onToggleExpanded,
  onUpdate,
  onDelete,
  allStrategies,
  allRecipes,
  problemSlug,
  functionNameOverride,
  forceExpandState,
}: {
  approach: ApproachWithHints | undefined
  approachIndex: number
  dragHandleProps: Record<string, unknown>
  isExpanded: boolean
  onToggleExpanded: () => void
  onUpdate: (updates: Partial<ApproachWithHints>) => void
  onDelete: () => void
  allStrategies: { id: string; name: string; slug: string }[]
  allRecipes?: RecipeSummary[]
  problemSlug?: string
  functionNameOverride?: string
  forceExpandState?: ForceExpandState | null
}) {
  if (!approach) {
    return (
      <div className="border-border rounded-lg border border-dashed p-4 opacity-50">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="cursor-grab touch-none active:cursor-grabbing"
            {...dragHandleProps}
            aria-label="Drag to reorder"
          >
            <GripVertical className="text-muted-foreground h-4 w-4" />
          </button>
          <span className="text-muted-foreground text-sm italic">
            Approach not found (may have been deleted)
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onDelete}
            className="text-muted-foreground hover:text-destructive p-1 transition-colors"
            aria-label="Delete block"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    )
  }

  // Use the ApproachBlockEditor for full editing capability
  return (
    <ApproachBlockEditor
      approach={approach}
      index={approachIndex}
      isExpanded={isExpanded}
      onToggleExpanded={onToggleExpanded}
      onUpdate={onUpdate}
      onDelete={onDelete}
      allStrategies={allStrategies}
      allRecipes={allRecipes}
      problemSlug={problemSlug}
      functionNameOverride={functionNameOverride}
      dragHandleProps={dragHandleProps}
      forceExpandState={forceExpandState}
    />
  )
}

// =============================================================================
// Approach Preview (reusable rendering component)
// =============================================================================

export function ApproachPreview({
  approach,
  approachNumber,
  language,
  recipes = [],
}: {
  approach: QuestionApproach
  approachNumber: number
  language: string
  recipes?: RecipeForCallout[]
}) {
  const typeConfig = SOLUTION_TYPES.find(
    (t) => t.value === approach.solutionType,
  )
  const matchingSolution = (approach.solutions || []).find(
    (s) => s.language === language,
  )
  const blocks = approach.blocks || []

  return (
    <div>
      {/* Approach heading with metadata on the right */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex shrink-0 items-center gap-3">
          {/* Approach number pill */}
          <span className="bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold">
            {approachNumber}
          </span>
          <h3 className="text-foreground whitespace-nowrap text-2xl font-semibold tracking-tight">
            {approach.name || 'Untitled'}
          </h3>
        </div>

        {/* Metadata badges: tags (left), difficulty (middle), solution type (right) */}
        <div className="flex flex-wrap items-center gap-2">
          {approach.strategies &&
            approach.strategies.length > 0 &&
            approach.strategies.map((s) => (
              <Badge key={s.id} variant="topic">
                {s.name}
              </Badge>
            ))}
          {approach.difficulty && (
            <Badge variant={approach.difficulty.name}>
              {approach.difficulty.name}
            </Badge>
          )}
          {approach.solutionType && typeConfig && (
            <Badge variant={approach.solutionType}>{typeConfig.label}</Badge>
          )}
        </div>
      </div>

      {/* Render blocks in order */}
      {blocks.length > 0 ? (
        blocks.map((block) => {
          if (block.type === 'markdown') {
            const parsed = preprocessImageTags(
              parseLanguageTags(block.content, language),
            )
            if (!parsed.trim()) return null
            return (
              <SyntaxHighlightedMarkdown
                key={block.id}
                className="prose prose-sm dark:prose-invert mb-6 max-w-none prose-headings:text-base prose-headings:font-medium prose-headings:tracking-tight prose-p:leading-relaxed"
                components={previewMarkdownComponents}
              >
                {parsed}
              </SyntaxHighlightedMarkdown>
            )
          }
          if (block.type === 'code') {
            // Use solution-level override if present, otherwise approach-level
            const hasOverride = matchingSolution?.complexities?.some(
              (c) => c.overridesApproach,
            )
            const complexities = hasOverride
              ? matchingSolution?.complexities || []
              : approach.complexities || []
            return (
              <div key={block.id} className="mb-6">
                {/* Implementation header */}
                <div className="mb-3 flex items-center gap-2">
                  <div className="from-sky-500 to-blue-500 h-5 w-1 rounded-full bg-gradient-to-b" />
                  <h4 className="text-foreground text-base font-semibold tracking-wide">
                    Implementation
                  </h4>
                </div>
                {matchingSolution ? (
                  <HighlightedCodeBlock
                    code={matchingSolution.code}
                    language={language === 'python' ? 'python' : 'javascript'}
                  />
                ) : (
                  <p className="text-muted-foreground text-sm italic">
                    No {language} solution available
                  </p>
                )}
                {/* Render complexity inline with code block (complexity is now per-solution) */}
                {complexities.length > 0 && (
                  <ComplexityAnalysis
                    complexities={complexities}
                    className="mt-4"
                  />
                )}
              </div>
            )
          }
          // Legacy: complexity blocks are deprecated, complexity is now rendered inline with code blocks
          if (block.type === 'complexity') {
            // Skip - complexity is now rendered as part of the code block above
            return null
          }
          if (block.type === 'recipe') {
            const recipe = recipes.find((r) => r.id === block.recipeId)
            if (!recipe) return null
            return (
              <RecipeCallout
                key={block.id}
                recipe={recipe}
                language={language}
                className="mb-6"
              />
            )
          }
          return null
        })
      ) : (
        <p className="text-muted-foreground text-sm italic">
          No content blocks yet.
        </p>
      )}
    </div>
  )
}

// =============================================================================
// Editorial Preview
// =============================================================================

function EditorialPreview({
  blocks,
  approaches,
  language,
  recipes = [],
}: {
  blocks: EditorialBlock[]
  approaches: QuestionApproach[]
  language: string
  recipes?: RecipeForCallout[]
}) {
  if (blocks.length === 0) {
    return (
      <div className="min-h-[400px] w-full px-3 py-2">
        <p className="text-muted-foreground text-sm italic">
          No editorial blocks yet. Switch to Edit mode to add content.
        </p>
      </div>
    )
  }

  // Track approach numbering
  let approachCounter = 0

  return (
    <div className="min-h-[400px] w-full space-y-8 px-6 py-4">
      {/* Solution heading */}
      <h2 className="text-foreground text-3xl font-bold tracking-tight">
        Solution
      </h2>

      {blocks.map((block, i) => {
        if (block.type === 'markdown') {
          const parsed = preprocessImageTags(
            parseLanguageTags(block.content, language),
          )
          if (!parsed.trim()) return null
          return (
            <SyntaxHighlightedMarkdown
              key={block.id}
              className="prose prose-sm dark:prose-invert mb-4 max-w-none last:mb-0"
              components={previewMarkdownComponents}
            >
              {parsed}
            </SyntaxHighlightedMarkdown>
          )
        }

        if (block.type === 'approach') {
          const approach = approaches.find((a) => a.id === block.approachId)
          if (!approach) return null
          approachCounter++

          // Check if previous block was also an approach (no double divider)
          const prevBlock = i > 0 ? blocks[i - 1] : null
          const needsDivider =
            !prevBlock || prevBlock.type !== 'approach' || i === 0

          return (
            <div key={block.id}>
              {/* Divider before approach */}
              {needsDivider && <hr className="border-border my-6" />}
              {!needsDivider && prevBlock?.type === 'approach' && (
                <hr className="border-border my-6" />
              )}

              <ApproachPreview
                approach={approach}
                approachNumber={approachCounter}
                language={language}
                recipes={recipes}
              />

              {/* Divider after last approach if next block is markdown (closing remarks) */}
              {i === blocks.length - 1 || blocks[i + 1]?.type === 'markdown' ? (
                <hr className="border-border my-6" />
              ) : null}
            </div>
          )
        }

        return null
      })}

      <Copyright scope="all" />
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function EditorialSection({
  editorialFormat = 'new',
  onUpdateEditorialFormat,
  editorialBlocks,
  approaches,
  onUpdateBlocks,
  onUpdateApproaches,
  allStrategies = [],
  allRecipes = [],
  recipesForPreview = [],
  problemSlug,
  functionNameOverride,
}: EditorialSectionProps) {
  const [activeTab, setActiveTab] = useState<TabId>('edit')
  const [previewLanguage, setPreviewLanguage] =
    useState<SolutionLanguage>('python')
  // Track which approach block is expanded (by block ID)
  const [expandedBlockId, setExpandedBlockId] = useState<string | null>(null)
  // Global expand/collapse control - increment generation to trigger change
  const [forceExpandState, setForceExpandState] =
    useState<ForceExpandState | null>(null)

  const handleExpandAll = () => {
    // Expand all approach blocks
    const allApproachBlockIds = editorialBlocks
      .filter((b) => b.type === 'approach')
      .map((b) => b.id)
    // For simplicity, expand all by setting expandedBlockId to a special value
    // We'll also trigger child component updates
    setForceExpandState((prev) => ({
      generation: (prev?.generation ?? 0) + 1,
      expanded: true,
    }))
    // Expand all approach blocks by tracking them all
    if (allApproachBlockIds.length > 0) {
      setExpandedBlockId('__all__')
    }
  }

  const handleCollapseAll = () => {
    setForceExpandState((prev) => ({
      generation: (prev?.generation ?? 0) + 1,
      expanded: false,
    }))
    setExpandedBlockId(null)
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = editorialBlocks.findIndex((b) => b.id === active.id)
      const newIndex = editorialBlocks.findIndex((b) => b.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      onUpdateBlocks(arrayMove(editorialBlocks, oldIndex, newIndex))
    },
    [editorialBlocks, onUpdateBlocks],
  )

  const addMarkdownAt = useCallback(
    (index: number) => {
      const newBlock: EditorialBlock = {
        type: 'markdown',
        id: crypto.randomUUID(),
        content: '',
      }
      const updated = [...editorialBlocks]
      updated.splice(index, 0, newBlock)
      onUpdateBlocks(updated)
    },
    [editorialBlocks, onUpdateBlocks],
  )

  // Create a new approach and add its block at the given index
  const addNewApproachAt = useCallback(
    (index: number) => {
      if (!onUpdateApproaches) return

      // Create new approach with default blocks
      const newApproachId = crypto.randomUUID()
      const newApproach: ApproachWithHints = {
        id: newApproachId,
        name: '',
        solutionType: 'acceptable',
        strategies: [],
        hints: [],
        solutions: [],
        blocks: [
          { type: 'markdown', id: crypto.randomUUID(), content: '' },
          { type: 'code', id: crypto.randomUUID() },
        ],
      }

      // Add approach to the list
      onUpdateApproaches([...approaches, newApproach])

      // Add editorial block referencing the new approach
      const newBlock: EditorialBlock = {
        type: 'approach',
        id: crypto.randomUUID(),
        approachId: newApproachId,
      }
      const updatedBlocks = [...editorialBlocks]
      updatedBlocks.splice(index, 0, newBlock)
      onUpdateBlocks(updatedBlocks)

      // Expand the new approach
      setExpandedBlockId(newBlock.id)
    },
    [editorialBlocks, approaches, onUpdateBlocks, onUpdateApproaches],
  )

  const updateMarkdownContent = useCallback(
    (blockId: string, content: string) => {
      onUpdateBlocks(
        editorialBlocks.map((b) =>
          b.id === blockId && b.type === 'markdown' ? { ...b, content } : b,
        ),
      )
    },
    [editorialBlocks, onUpdateBlocks],
  )

  // Update an approach by its ID
  const updateApproachById = useCallback(
    (approachId: string, updates: Partial<ApproachWithHints>) => {
      if (!onUpdateApproaches) return
      onUpdateApproaches(
        approaches.map((a) => (a.id === approachId ? { ...a, ...updates } : a)),
      )
    },
    [approaches, onUpdateApproaches],
  )

  // Delete a block (and the associated approach if it's an approach block)
  const deleteBlock = useCallback(
    (blockId: string, approachId?: string) => {
      onUpdateBlocks(editorialBlocks.filter((b) => b.id !== blockId))

      // Also delete the approach if specified
      if (approachId && onUpdateApproaches) {
        onUpdateApproaches(approaches.filter((a) => a.id !== approachId))
      }

      // Clear expanded state if this block was expanded
      if (expandedBlockId === blockId) {
        setExpandedBlockId(null)
      }
    },
    [
      editorialBlocks,
      approaches,
      onUpdateBlocks,
      onUpdateApproaches,
      expandedBlockId,
    ],
  )

  // Calculate approach number based on position in editorial
  const getApproachNumber = useCallback(
    (blockId: string) => {
      let count = 0
      for (const block of editorialBlocks) {
        if (block.type === 'approach') {
          count++
          if (block.id === blockId) {
            return count
          }
        }
      }
      return count
    },
    [editorialBlocks],
  )

  const isLegacy = editorialFormat === 'legacy'

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Editorial</h2>
        <p className="text-muted-foreground text-sm">
          Compose the editorial by interleaving markdown blocks with approach
          references. Use the preview to see how it will render.
        </p>
      </div>

      {/* Editorial format toggle */}
      {onUpdateEditorialFormat && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium">Format:</label>
          <div className="flex items-center gap-1 rounded-md border p-0.5">
            <button
              type="button"
              onClick={() => onUpdateEditorialFormat('new')}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                !isLegacy
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              New
            </button>
            <button
              type="button"
              onClick={() => onUpdateEditorialFormat('legacy')}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                isLegacy
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Legacy
            </button>
          </div>
        </div>
      )}

      {/* Legacy format banner */}
      {isLegacy && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3">
          <p className="text-sm text-amber-700 dark:text-amber-400">
            This problem uses a <strong>legacy editorial</strong> synced from
            bctci-code. Run{' '}
            <code className="rounded bg-amber-500/20 px-1">
              pnpm sync:legacy-editorials
            </code>{' '}
            to update it. The editorial block editor below is not used for
            legacy editorials.
          </p>
        </div>
      )}

      {/* Tab buttons */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setActiveTab('edit')}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            activeTab === 'edit'
              ? 'bg-muted text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('preview')}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            activeTab === 'preview'
              ? 'bg-muted text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Preview
        </button>

        {/* Expand/Collapse buttons (edit only) */}
        {activeTab === 'edit' && (
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={handleExpandAll}
              className="text-muted-foreground hover:text-foreground rounded-md px-2 py-1 text-xs transition-colors"
            >
              Expand All
            </button>
            <button
              type="button"
              onClick={handleCollapseAll}
              className="text-muted-foreground hover:text-foreground rounded-md px-2 py-1 text-xs transition-colors"
            >
              Collapse All
            </button>
          </div>
        )}

        {/* Language selector (preview only) */}
        {activeTab === 'preview' && (
          <select
            value={previewLanguage}
            onChange={(e) =>
              setPreviewLanguage(e.target.value as SolutionLanguage)
            }
            className="border-input bg-background ml-auto rounded-md border px-2 py-1 text-sm"
          >
            {SUPPORTED_SOLUTION_LANGUAGES.map((lang) => (
              <option key={lang.value} value={lang.value}>
                {lang.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Content */}
      {activeTab === 'edit' ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={editorialBlocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            {/* Insert button at top */}
            <InsertButton
              onAddMarkdown={() => addMarkdownAt(0)}
              onAddNewApproach={() => addNewApproachAt(0)}
            />

            {editorialBlocks.map((block, index) => (
              <div key={block.id}>
                <SortableBlock id={block.id}>
                  {({ dragHandleProps, isDragging: _isDragging }) => {
                    if (block.type === 'markdown') {
                      return (
                        <CollapsibleMarkdownEditor
                          content={block.content}
                          onChange={(content) =>
                            updateMarkdownContent(block.id, content)
                          }
                          onDelete={() => deleteBlock(block.id)}
                          dragHandleProps={dragHandleProps}
                          forceExpandState={forceExpandState}
                        />
                      )
                    }
                    if (block.type === 'approach') {
                      const approach = approaches.find(
                        (a) => a.id === block.approachId,
                      )
                      const approachNumber = getApproachNumber(block.id)
                      return (
                        <InlineApproachBlockCard
                          approach={approach}
                          approachIndex={approachNumber - 1}
                          dragHandleProps={dragHandleProps}
                          isExpanded={
                            expandedBlockId === '__all__' ||
                            expandedBlockId === block.id
                          }
                          onToggleExpanded={() =>
                            setExpandedBlockId(
                              expandedBlockId === block.id ||
                                expandedBlockId === '__all__'
                                ? null
                                : block.id,
                            )
                          }
                          forceExpandState={forceExpandState}
                          onUpdate={(updates) =>
                            updateApproachById(block.approachId, updates)
                          }
                          onDelete={() =>
                            deleteBlock(block.id, block.approachId)
                          }
                          allStrategies={allStrategies}
                          allRecipes={allRecipes}
                          problemSlug={problemSlug}
                          functionNameOverride={functionNameOverride}
                        />
                      )
                    }
                    return null
                  }}
                </SortableBlock>

                {/* Insert button after each block */}
                <InsertButton
                  onAddMarkdown={() => addMarkdownAt(index + 1)}
                  onAddNewApproach={() => addNewApproachAt(index + 1)}
                />
              </div>
            ))}

            {editorialBlocks.length === 0 && (
              <div className="border-border rounded-lg border border-dashed p-8 text-center">
                <p className="text-muted-foreground text-sm">
                  No blocks yet. Click the + button above to add markdown or
                  approach blocks.
                </p>
              </div>
            )}
          </SortableContext>
        </DndContext>
      ) : (
        <EditorialPreview
          blocks={editorialBlocks}
          approaches={approaches}
          language={previewLanguage}
          recipes={recipesForPreview}
        />
      )}

      <div className="text-muted-foreground text-xs">
        <strong>Tip:</strong> Use{' '}
        <code className="bg-muted rounded px-1 py-0.5">
          {'{py}'}...{'{/py}'}
        </code>{' '}
        or{' '}
        <code className="bg-muted rounded px-1 py-0.5">
          {'{python}'}...{'{/python}'}
        </code>{' '}
        for language-specific content. Shorthands: py, js, cpp, java.
      </div>
    </div>
  )
}
